import { router } from "@inertiajs/react";
import { type BeforeSaveDataDetails } from '@revolist/react-datagrid';
import type { MutableRefObject } from "react";
import { useEffect } from "react";

type ValueProcessor<T> = (prop: keyof T, newValue: unknown, currentValue: unknown) => string | number | null;
type PathGenerator<T> = (model: T) => string;

interface UseRevoGridEditOptions<T> {
  gridRef: MutableRefObject<HTMLRevoGridElement | null>;
  pathGenerator: PathGenerator<T>;
  resourceKey: string;
  valueProcessor?: ValueProcessor<T>;
}

/**
 * Reusable hook for handling RevoGrid cell editing with backend updates
 * 
 * @param options Configuration options
 * @param options.gridRef - Ref to the RevoGrid element
 * @param options.pathGenerator - Function to generate update path for a model
 * @param options.resourceKey - Key name for wrapping the data in PATCH request (e.g. 'supplier', 'quote_item')
 * @param options.valueProcessor - Optional custom value processor
 */
export function useRevoGridEdit<T extends object>(options: UseRevoGridEditOptions<T>) {
  const { gridRef, pathGenerator, resourceKey, valueProcessor } = options;

  useEffect(() => {
    const handleBeforeEdit = (e: Event) => {
      // Extract data from RevoGrid beforeedit event
      const detail = (e as CustomEvent<BeforeSaveDataDetails>).detail;
      const model = detail.model as T;
      const prop = detail.prop as keyof T;
      const newValue = detail.val as unknown;
      
      // Get current value for comparison
      const currentValue: unknown = (model as Record<PropertyKey, unknown>)[prop];
      
      // Process new value based on column type
      let processedValue: string | number | null;
      
      if (valueProcessor) {
        processedValue = valueProcessor(prop, newValue, currentValue);
      } else {
        // Default processing
        processedValue = newValue as string | number | null;
      }
      
      // Prevent unnecessary calls if value hasn't changed
      if (currentValue === processedValue) {
        return;
      }
      
      // Send update to backend asynchronously
      router.patch(
        pathGenerator(model),
        { [resourceKey]: { [String(prop)]: processedValue } },
        {
          preserveScroll: true,
          onError: (errors) => {
            console.error(`Error updating ${String(prop)}:`, errors);
            // TODO: Add toast notification for user feedback
          },
          onSuccess: () => {
            // Update successful, Inertia refreshes automatically
          }
        }
      );
    };

    const gridEl = gridRef.current;
    if (gridEl) {
      gridEl.addEventListener('beforeedit', handleBeforeEdit);
      return () => gridEl.removeEventListener('beforeedit', handleBeforeEdit);
    }
  }, []); // Empty dependency array - event handler doesn't need to recreate
}